import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import authorizedAxios from "../../helpers/authorizedAxios";
import PATH_URL from "../../routers/path";
import AkasSelect from "../../components/AkasSelect";
import AiVisualizations from "./components/AiVisualizations";
import "./ai-assistant.css";

const quickReplies = [
  "Apa yang harus saya lakukan hari ini?",
  "Tampilkan grafik penjualan minggu ini.",
  "Tampilkan 5 menu terlaris bulan ini.",
  // "Berikan health score bisnis saya bulan ini.",
  // "Kenapa omzet saya turun bulan ini?",
  // "Kenapa profit saya turun bulan ini?",
  // "Kalau harga Kopi naik Rp2.000 bagaimana?",
  // "Buatkan plan omzet Rp100 juta bulan depan.",
  "Produk apa yang paling sering dibeli bersamaan bulan ini?",
  "Pelanggan mana yang berisiko tidak kembali?",
  // "Buatkan promo dari produk yang penjualannya turun.",
  "Bandingkan biaya material dari supplier saya.",
  // "Apakah pemakaian bahan sesuai resep bulan ini?",
  "Bagaimana proyeksi arus kas 30 hari ke depan?",
  // "Berapa umur piutang dan utang saya saat ini?",
  // "Berapa omzet hari ini?",
  // "Produk apa yang trennya turun bulan ini?",
  "Berapa laba kotor bulan ini?",
  // "Bandingkan performa semua outlet bulan ini.",
  // "Stok apa yang berlebih atau tidak bergerak?",
  // "Kasir mana yang omzetnya paling tinggi minggu ini?",
  // "Apa hal menarik dari transaksi penjualan dan pembelian minggu ini?",
  // "Forecast stok untuk 30 hari ke depan.",
  "Bagaimana cara membuat resep di AKAS?",
];

const markdownLink = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/i;

const renderInlineMarkdown = (value, keyPrefix = "inline") => {
  const tokenPattern =
    /(\*\*[^*\n]+?\*\*|__[^_\n]+?__|~~[^~\n]+?~~|`[^`\n]+?`|\[[^\]]+\]\(https?:\/\/[^\s)]+\)|\*[^*\n]+?\*|_[^_\n]+?_)/g;
  const parts = String(value || "").split(tokenPattern);

  return parts.map((part, index) => {
    if (!part) return null;

    const key = `${keyPrefix}-${index}`;
    const link = part.match(markdownLink);
    if (link)
      return (
        <a key={key} href={link[2]} target="_blank" rel="noreferrer">
          {renderInlineMarkdown(link[1], `${key}-link`)}
        </a>
      );
    if ((part.startsWith("**") && part.endsWith("**")) || (part.startsWith("__") && part.endsWith("__")))
      return <strong key={key}>{renderInlineMarkdown(part.slice(2, -2), `${key}-strong`)}</strong>;
    if (part.startsWith("~~") && part.endsWith("~~"))
      return <del key={key}>{renderInlineMarkdown(part.slice(2, -2), `${key}-delete`)}</del>;
    if (part.startsWith("`") && part.endsWith("`")) return <code key={key}>{part.slice(1, -1)}</code>;
    if ((part.startsWith("*") && part.endsWith("*")) || (part.startsWith("_") && part.endsWith("_")))
      return <em key={key}>{renderInlineMarkdown(part.slice(1, -1), `${key}-emphasis`)}</em>;
    return part;
  });
};

const isListItem = (line) => /^(?:[-+*]|\d+[.)])\s+/.test(line);

const AiMarkdown = ({ content }) => {
  const lines = String(content || "")
    .replace(/\r\n?/g, "\n")
    .split("\n");
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const language = line.slice(3).trim();
      const code = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith("```")) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push(
        <pre key={`code-${index}`}>
          <code className={language ? `language-${language}` : undefined}>{code.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const Tag = `h${heading[1].length}`;
      blocks.push(<Tag key={`heading-${index}`}>{renderInlineMarkdown(heading[2], `heading-${index}`)}</Tag>);
      index += 1;
      continue;
    }

    if (/^(?:---+|\*\*\*+|___+)$/.test(line.trim())) {
      blocks.push(<hr key={`divider-${index}`} />);
      index += 1;
      continue;
    }

    if (line.startsWith(">")) {
      const quote = [];
      while (index < lines.length && lines[index].startsWith(">")) {
        quote.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push(
        <blockquote key={`quote-${index}`}>{renderInlineMarkdown(quote.join("\n"), `quote-${index}`)}</blockquote>,
      );
      continue;
    }

    if (isListItem(line)) {
      const ordered = /^\d+[.)]\s+/.test(line);
      const items = [];
      while (index < lines.length && isListItem(lines[index]) && /^\d+[.)]\s+/.test(lines[index]) === ordered) {
        items.push(lines[index].replace(ordered ? /^\d+[.)]\s+/ : /^[-+*]\s+/, ""));
        index += 1;
      }
      const List = ordered ? "ol" : "ul";
      const listIndex = index;
      blocks.push(
        <List key={`list-${listIndex}`}>
          {items.map((item, itemIndex) => (
            <li key={`item-${itemIndex}`}>{renderInlineMarkdown(item, `list-${listIndex}-${itemIndex}`)}</li>
          ))}
        </List>,
      );
      continue;
    }

    const paragraph = [line];
    index += 1;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !lines[index].startsWith("```") &&
      !lines[index].startsWith(">") &&
      !isListItem(lines[index]) &&
      !/^#{1,3}\s+/.test(lines[index]) &&
      !/^(?:---+|\*\*\*+|___+)$/.test(lines[index].trim())
    ) {
      paragraph.push(lines[index]);
      index += 1;
    }
    const paragraphIndex = index;
    blocks.push(
      <p key={`paragraph-${paragraphIndex}`}>
        {paragraph.map((part, lineIndex) => (
          <React.Fragment key={`line-${lineIndex}`}>
            {lineIndex ? <br /> : null}
            {renderInlineMarkdown(part, `paragraph-${paragraphIndex}-${lineIndex}`)}
          </React.Fragment>
        ))}
      </p>,
    );
  }

  return <>{blocks}</>;
};

const dateTime = (value) => {
  if (!value) return "";

  return new Intl.DateTimeFormat("id-ID", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
};

const relativeDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();

  return sameDay ? dateTime(value) : new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short" }).format(date);
};

const remainingThisWeek = (status) => Number(status?.remaining_this_week ?? status?.remaining_today ?? 0);

const UsageBadge = ({ status }) => {
  if (!status) return <span className="placeholder col-12" aria-label="Memuat kuota AI" />;
  if (status.is_unlimited) return <span className="ai-assistant-usage">Tanpa batas</span>;

  const limit = Math.max(0, Number(status.limit) || 0);
  const remaining = Math.min(limit, Math.max(0, remainingThisWeek(status)));
  const progress = limit > 0 ? Math.round((remaining / limit) * 100) : 0;

  return (
    <div
      className="d-flex flex-column gap-1"
      style={{
        width: "100%",
      }}>
      <span className="fs-6 fw-semibold ms-1">
        {remaining} dari {limit} tersisa minggu ini
      </span>

      <span
        className="ai-assistant-usage w-100"
        role="status"
        aria-label={`Sisa kuota Akasya minggu ini: ${remaining} dari ${limit}`}>
        <span className="ai-assistant-usage-progress w-100 " aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </span>
      </span>
    </div>
  );
};

const actionTypeLabel = {
  restock_purchase: "Draft restock",
  marketing_campaign: "Draft marketing",
  product_price_change: "Draft perubahan harga",
};

const actionStatusLabel = {
  draft: "Menunggu konfirmasi",
  confirmed: "Sudah dikonfirmasi",
  cancelled: "Dibatalkan",
  expired: "Kedaluwarsa",
};

const feedbackReasons = [
  { value: "incorrect_answer", label: "Jawaban salah" },
  { value: "incorrect_data", label: "Data tidak sesuai" },
  { value: "misunderstood_question", label: "Pertanyaan tidak dipahami" },
  { value: "incomplete_answer", label: "Kurang lengkap" },
  { value: "too_complicated", label: "Terlalu berbelit" },
  { value: "tool_failed", label: "Data gagal dimuat" },
  { value: "other", label: "Lainnya" },
];

const copyToClipboard = async (value) => {
  let clipboardError = null;

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch (error) {
      clipboardError = error;
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);

  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const copied = typeof document.execCommand === "function" && document.execCommand("copy");
    if (!copied) throw new Error("Perintah salin tidak didukung oleh browser.");
  } catch (fallbackError) {
    const error = new Error("Clipboard tidak tersedia.");
    error.cause = clipboardError || fallbackError;
    throw error;
  } finally {
    textarea.remove();
  }
};

const MessageActions = ({ message, copied, busy, onCopy, onRate, onExplain }) => {
  const isAssistant = message.role === "assistant";
  const rating = message.feedback?.rating;

  return (
    <div className="ai-assistant-message-actions" aria-label="Aksi pesan">
      <button
        type="button"
        className={copied ? "is-active" : ""}
        aria-label={isAssistant ? "Salin balasan Akasya" : "Salin pesan Anda"}
        title={copied ? "Tersalin" : "Salin"}
        onClick={() => onCopy(message)}>
        {copied ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-copy-check">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667l0 -8.666" />
            <path d="M4.012 16.737a2 2 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
            <path d="M11 14l2 2l4 -4" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-copy">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667l0 -8.666" />
            <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
          </svg>
        )}
      </button>
      {isAssistant ? (
        <>
          <button
            type="button"
            className={rating === "helpful" ? "is-active is-helpful" : ""}
            aria-label="Jawaban membantu"
            aria-pressed={rating === "helpful"}
            title="Jawaban membantu"
            disabled={busy}
            onClick={() => onRate(message, "helpful")}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-thumb-up">
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M7 11v8a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-7a1 1 0 0 1 1 -1h3a4 4 0 0 0 4 -4v-1a2 2 0 0 1 4 0v5h3a2 2 0 0 1 2 2l-1 5a2 3 0 0 1 -2 2h-7a3 3 0 0 1 -3 -3" />
            </svg>
          </button>
          <button
            type="button"
            className={rating === "not_helpful" ? "is-active is-not-helpful" : ""}
            aria-label="Jawaban kurang membantu"
            aria-pressed={rating === "not_helpful"}
            title="Jawaban kurang membantu"
            disabled={busy}
            onClick={() => onRate(message, "not_helpful")}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-thumb-down">
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2 -2l-1 -5a2 3 0 0 0 -2 -2h-7a3 3 0 0 0 -3 3" />
            </svg>
          </button>
          {rating === "not_helpful" ? (
            <button
              type="button"
              className="ai-assistant-feedback-explain"
              aria-label="Tambahkan detail evaluasi"
              title="Tambahkan detail"
              disabled={busy}
              onClick={() => onExplain(message)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-message-dots">
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M12 11v.01" />
                <path d="M8 11v.01" />
                <path d="M16 11v.01" />
                <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3l12 0" />
              </svg>
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const FeedbackDetailPanel = ({ draft, busy, error, onChange, onSave, onClose }) => (
  <div className="ai-assistant-feedback-panel" role="group" aria-label="Detail evaluasi jawaban Akasya">
    <div className="ai-assistant-feedback-panel-head">
      <div>
        <strong>Apa yang kurang tepat?</strong>
        <span>Opsional, tapi sangat membantu kami mengevaluasi Akasya.</span>
      </div>
      <button type="button" aria-label="Tutup detail evaluasi" onClick={onClose}>
        <i className="ti ti-x" />
      </button>
    </div>
    <div className="ai-assistant-feedback-reasons">
      {feedbackReasons.map((reason) => (
        <button
          type="button"
          key={reason.value}
          className={draft.reason_code === reason.value ? "is-active" : ""}
          aria-pressed={draft.reason_code === reason.value}
          onClick={() => onChange("reason_code", draft.reason_code === reason.value ? "" : reason.value)}>
          {reason.label}
        </button>
      ))}
    </div>
    <textarea
      rows="2"
      maxLength="1000"
      value={draft.comment}
      aria-label="Catatan evaluasi opsional"
      placeholder="Tambahkan catatan jika perlu..."
      onChange={(event) => onChange("comment", event.target.value)}
    />
    {error ? (
      <div className="ai-assistant-feedback-error" role="alert">
        <i className="ti ti-alert-circle" aria-hidden="true" />
        <span>{error}</span>
      </div>
    ) : null}
    <div className="ai-assistant-feedback-panel-actions">
      <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={onSave}>
        {busy ? <span className="spinner-border spinner-border-sm" /> : <i className="ti ti-check" />} Simpan detail
      </button>
      <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={onClose}>
        Nanti saja
      </button>
    </div>
  </div>
);

const messageActionDrafts = (message) => {
  const drafts = (message?.tool_calls || []).map((toolCall) => toolCall?.action_draft).filter(Boolean);
  return drafts.filter((draftItem, index) => drafts.findIndex((item) => item.id === draftItem.id) === index);
};

const ActionDraftCard = ({ action, busy, onDecision, canConfirm = false }) => {
  const payload = action?.payload || {};
  const items = Array.isArray(payload.items) ? payload.items : [];

  return (
    <div className={`ai-action-draft ai-action-draft-${action.status || "draft"}`}>
      <div className="ai-action-draft-head">
        <span>
          <i className="ti ti-file-check" /> {actionTypeLabel[action.action_type] || "Draft action"}
        </span>
        <small>{actionStatusLabel[action.status] || action.status}</small>
      </div>
      <strong>{action.title}</strong>
      {action.reason ? <p>{action.reason}</p> : null}
      {items.length ? (
        <ul>
          {items.slice(0, 6).map((item, index) => (
            <li key={`${action.id}-item-${item.material_id || item.product_id || index}`}>
              <span>{item.material_name || item.product_name || "Item"}</span>
              <b>
                {item.quantity != null ? `${item.quantity} ${item.unit || ""}` : null}
                {item.new_price != null ? `Rp${Number(item.new_price).toLocaleString("id-ID")}` : null}
              </b>
            </li>
          ))}
        </ul>
      ) : null}
      {payload.content ? <div className="ai-action-draft-copy">{payload.content}</div> : null}
      {action.status === "draft" ? (
        <div className="ai-action-draft-actions">
          {canConfirm ? <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy}
            onClick={() => onDecision(action.id, "confirm")}>
            {busy ? <span className="spinner-border spinner-border-sm" /> : <i className="ti ti-check" />} Konfirmasi
            draft
          </button> : null}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={busy}
            onClick={() => onDecision(action.id, "cancel")}>
            Batalkan
          </button>
        </div>
      ) : null}
      <small className="ai-action-draft-note">
        {action.status === "confirmed"
          ? "Terkonfirmasi untuk tindak lanjut manual; belum mengubah data bisnis."
          : "Konfirmasi tidak langsung mengeksekusi perubahan bisnis."}
      </small>
    </div>
  );
};

const AiAssistantPage = () => {
  const [status, setStatus] = useState(null);
  const [scopeOptions, setScopeOptions] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [updatingActionId, setUpdatingActionId] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [updatingFeedbackId, setUpdatingFeedbackId] = useState(null);
  const [feedbackDraft, setFeedbackDraft] = useState({ messageId: null, reason_code: "", comment: "" });
  const [feedbackError, setFeedbackError] = useState({ messageId: null, message: "" });
  const messagesEndRef = useRef(null);
  const copyTimerRef = useRef(null);

  const canSend = Boolean(
    status?.available && Boolean(selectedScope) && (status?.is_unlimited || remainingThisWeek(status) > 0) && !sending,
  );

  const refreshStatus = useCallback(async () => {
    const response = await authorizedAxios.get("/ai/status");
    setStatus(response?.data?.data || null);
  }, []);

  const loadConversations = useCallback(async () => {
    const response = await authorizedAxios.get("/ai/conversations");
    setConversations(response?.data?.data?.conversations || []);
  }, []);

  const loadScopes = useCallback(async () => {
    const response = await authorizedAxios.get("/ai/scopes");
    const data = response?.data?.data || null;
    const canSelectAll = Boolean(data?.can_select_all && (data?.outlets || []).length > 1);
    const defaultScope = canSelectAll
      ? {
          type: "all",
          outlet_id: null,
          outlet_ids: data.outlets.map((outlet) => Number(outlet.id)),
          label: `Semua outlet (${data.outlets.length})`,
          outlets: data.outlets,
        }
      : data?.default_scope || null;
    setScopeOptions(data);
    setSelectedScope((current) => current || defaultScope);
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([refreshStatus(), loadScopes(), loadConversations()]);
    } catch (requestError) {
      setError(requestError?.userMessage || requestError?.response?.data?.message || "Akasya belum dapat dimuat.");
    } finally {
      setLoading(false);
    }
  }, [loadConversations, loadScopes, refreshStatus]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);
  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === "function") {
      messagesEndRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [messages, sending]);
  useEffect(() => {
    if (!isHistoryOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsHistoryOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);

    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isHistoryOpen]);
  useEffect(
    () => () => {
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const selectConversation = async (conversationId) => {
    if (!conversationId) return;
    if (conversationId === conversation?.id) {
      setIsHistoryOpen(false);
      return;
    }
    setIsHistoryOpen(false);
    setError("");
    try {
      const response = await authorizedAxios.get(`/ai/conversations/${conversationId}/messages`);
      const data = response?.data?.data;
      setConversation(data?.conversation || null);
      setMessages(data?.messages || []);
      if (data?.conversation?.scope) setSelectedScope(data.conversation.scope);
    } catch (requestError) {
      setError(requestError?.userMessage || requestError?.response?.data?.message || "Percakapan belum dapat dimuat.");
    }
  };

  const startConversation = () => {
    setIsHistoryOpen(false);
    setConversation(null);
    setMessages([]);
    setDraft("");
    setError("");
  };

  const changeScope = (event) => {
    const value = event.target.value;
    const nextScope =
      value === "all"
        ? { type: "all", outlet_id: null, label: "Semua outlet" }
        : (scopeOptions?.outlets || []).find((outlet) => Number(outlet.id) === Number(value));
    const normalizedScope =
      nextScope &&
      (nextScope.type === "all"
        ? nextScope
        : { type: "outlet", outlet_id: Number(nextScope.id), label: nextScope.name });
    if (
      !normalizedScope ||
      (normalizedScope.type === selectedScope?.type &&
        Number(normalizedScope.outlet_id) === Number(selectedScope?.outlet_id))
    )
      return;

    setSelectedScope(normalizedScope);
    startConversation();
  };

  const sendMessage = async (message = draft) => {
    const text = message.trim();
    if (!text || !canSend) return;

    const temporaryId = `temporary-${Date.now()}`;
    setSending(true);
    setError("");
    setDraft("");
    setMessages((current) => [
      ...current,
      { id: temporaryId, role: "user", content: text, created_at: new Date().toISOString(), status: "sending" },
    ]);

    try {
      const response = await authorizedAxios.post("/ai/messages", {
        message: text,
        conversation_id: conversation?.id || null,
        scope_type: selectedScope?.type || "outlet",
        outlet_id: selectedScope?.type === "outlet" ? selectedScope.outlet_id : null,
      });
      const data = response?.data?.data;
      const nextConversation = data?.conversation;
      setConversation(nextConversation || null);
      if (nextConversation?.scope) setSelectedScope(nextConversation.scope);
      setMessages((current) =>
        [
          ...current.map((item) => (item.id === temporaryId ? { ...item, status: "completed" } : item)),
          data?.message,
        ].filter(Boolean),
      );
      setConversations((current) => {
        const remaining = current.filter((item) => item.id !== nextConversation?.id);
        return nextConversation ? [nextConversation, ...remaining] : remaining;
      });
      setStatus((current) => (data?.usage ? { ...current, ...data.usage } : current));
    } catch (requestError) {
      const nextStatus = requestError?.response?.data?.data;
      if (["ai.weekly_messages", "ai.daily_messages"].includes(nextStatus?.entitlement)) setStatus(nextStatus);
      setMessages((current) => current.map((item) => (item.id === temporaryId ? { ...item, status: "failed" } : item)));
      setDraft(text);
      setError(
        requestError?.userMessage ||
          requestError?.response?.data?.message ||
          "Pesan belum dapat dikirim. Silakan coba lagi.",
      );
    } finally {
      setSending(false);
    }
  };

  const updateActionDraft = async (actionId, decision) => {
    if (!actionId || updatingActionId || (decision === "confirm" && !status?.can_confirm_actions)) return;
    setUpdatingActionId(actionId);
    setError("");
    try {
      const response = await authorizedAxios.post(
        `/ai/actions/${actionId}/${decision}`,
        decision === "confirm" ? { confirmation: true } : {},
      );
      const updatedAction = response?.data?.data?.action;
      if (!updatedAction) return;
      setMessages((current) =>
        current.map((message) => ({
          ...message,
          tool_calls: (message.tool_calls || []).map((toolCall) =>
            toolCall?.action_draft?.id === actionId ? { ...toolCall, action_draft: updatedAction } : toolCall,
          ),
        })),
      );
    } catch (requestError) {
      setError(
        requestError?.userMessage || requestError?.response?.data?.message || "Status draft belum dapat diperbarui.",
      );
    } finally {
      setUpdatingActionId(null);
    }
  };

  const copyMessage = async (message) => {
    try {
      await copyToClipboard(String(message?.content || ""));
      setCopiedMessageId(message.id);
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopiedMessageId(null), 1800);
      toast.success("Pesan disalin.");
    } catch (_copyError) {
      toast.error("Pesan belum dapat disalin.");
    }
  };

  const persistFeedback = async (messageId, payload, successMessage) => {
    if (!messageId || updatingFeedbackId) return null;
    setUpdatingFeedbackId(messageId);
    setFeedbackError({ messageId: null, message: "" });
    setError("");
    try {
      const response = await authorizedAxios.put(`/ai/messages/${messageId}/feedback`, payload);
      const feedback = response?.data?.data?.feedback || null;
      setMessages((current) =>
        current.map((message) => (message.id === messageId ? { ...message, feedback } : message)),
      );
      if (successMessage) toast.success(successMessage);
      return feedback;
    } catch (requestError) {
      const message =
        requestError?.userMessage ||
        requestError?.response?.data?.message ||
        "Evaluasi jawaban Akasya belum dapat disimpan.";
      setFeedbackError({ messageId, message });
      toast.error(message);
      return null;
    } finally {
      setUpdatingFeedbackId(null);
    }
  };

  const rateMessage = async (message, rating) => {
    if (!message?.id || updatingFeedbackId) return;
    if (message.feedback?.rating === rating) {
      setUpdatingFeedbackId(message.id);
      setFeedbackError({ messageId: null, message: "" });
      setError("");
      try {
        await authorizedAxios.delete(`/ai/messages/${message.id}/feedback`);
        setMessages((current) =>
          current.map((currentMessage) =>
            currentMessage.id === message.id ? { ...currentMessage, feedback: null } : currentMessage,
          ),
        );
        setFeedbackDraft({ messageId: null, reason_code: "", comment: "" });
        toast.success("Evaluasi dihapus.");
      } catch (requestError) {
        const errorMessage =
          requestError?.userMessage ||
          requestError?.response?.data?.message ||
          "Evaluasi jawaban Akasya belum dapat dihapus.";
        setFeedbackError({ messageId: message.id, message: errorMessage });
        toast.error(errorMessage);
      } finally {
        setUpdatingFeedbackId(null);
      }
      return;
    }

    if (rating === "not_helpful") {
      setFeedbackDraft({
        messageId: message.id,
        reason_code: message.feedback?.reason_code || "",
        comment: message.feedback?.comment || "",
      });
    } else {
      setFeedbackDraft({ messageId: null, reason_code: "", comment: "" });
    }

    const feedback = await persistFeedback(
      message.id,
      { rating },
      rating === "helpful" ? "Terima kasih atas evaluasinya." : "Evaluasi tersimpan.",
    );
    if (rating === "helpful" && !feedback) setFeedbackDraft({ messageId: null, reason_code: "", comment: "" });
  };

  const explainFeedback = (message) => {
    setFeedbackError({ messageId: null, message: "" });
    setFeedbackDraft({
      messageId: message.id,
      reason_code: message.feedback?.reason_code || "",
      comment: message.feedback?.comment || "",
    });
  };

  const saveFeedbackDetails = async () => {
    if (!feedbackDraft.messageId) return;
    const feedback = await persistFeedback(
      feedbackDraft.messageId,
      {
        rating: "not_helpful",
        reason_code: feedbackDraft.reason_code || null,
        comment: feedbackDraft.comment.trim() || null,
      },
      "Detail evaluasi tersimpan.",
    );
    if (feedback) setFeedbackDraft({ messageId: null, reason_code: "", comment: "" });
  };

  const updateFeedbackDraft = (field, value) => {
    setFeedbackError({ messageId: null, message: "" });
    setFeedbackDraft((current) => ({ ...current, [field]: value }));
  };

  const closeFeedbackDetails = () => {
    setFeedbackError({ messageId: null, message: "" });
    setFeedbackDraft({ messageId: null, reason_code: "", comment: "" });
  };

  const promptHint = useMemo(() => {
    if (!selectedScope) return "Menyiapkan scope data Akasya...";
    if (!status?.available) return "Akasya belum tersedia pada paket ini.";
    if (!status?.is_unlimited && remainingThisWeek(status) <= 0) return "Kuota AI minggu ini telah habis.";
    return "Estimasikan omzet pada bulan depan ...";
  }, [selectedScope, status]);

  return (
    <div className="page-wrapper ai-assistant-page">
      {/* <div className="page-header d-print-none mt-0">
        <div className="container-xl">
          <div className="ai-assistant-header">
            <div>
              <div className="ai-assistant-eyebrow">
                <i className="ti ti-sparkles" /> ASISTEN BISNIS
              </div>
              <h2 className="page-title">Akasya</h2>
            </div>
            <div className="ai-assistant-header-actions">
              <UsageBadge status={status} />
              <span className="ai-assistant-outlet">
                <i className="ti ti-building-store" /> {selectedScope?.label || "Memuat scope"}
              </span>
            </div>
          </div>
        </div>
      </div> */}

      <div className="page-body py-0">
        <div className="container-xl">
          {error ? (
            <div className="alert alert-danger ai-assistant-alert" role="alert">
              {error}
            </div>
          ) : null}

          <div className="ai-assistant-shell">
            {isHistoryOpen ? (
              <button
                type="button"
                className="ai-assistant-history-backdrop"
                aria-label="Tutup panel riwayat"
                onClick={() => setIsHistoryOpen(false)}
              />
            ) : null}
            <aside
              id="ai-assistant-history "
              className={`ai-assistant-sidebar ${isHistoryOpen ? "is-open" : ""}`}
              aria-label="Daftar percakapan">
              <div className="ai-assistant-sidebar-drawer-head">
                <span>Riwayat chat</span>
                <button type="button" onClick={() => setIsHistoryOpen(false)} aria-label="Tutup riwayat percakapan">
                  <i className="ti ti-x" />
                </button>
              </div>
              <button type="button" className="btn btn-primary ai-assistant-new-chat" onClick={startConversation}>
                <i className="ti ti-edit" /> Percakapan baru
              </button>
              <div className="ai-assistant-sidebar-label">Percakapan</div>
              {loading ? (
                <div className="placeholder-glow ai-assistant-list-loading">
                  <span className="placeholder col-11" />
                  <span className="placeholder col-8" />
                  <span className="placeholder col-9" />
                </div>
              ) : null}
              {!loading && conversations.length === 0 ? (
                <p className="ai-assistant-empty-list">Pertanyaan Anda akan tersimpan di sini.</p>
              ) : null}
              <div className="ai-assistant-conversation-list mb-5">
                {conversations.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`ai-assistant-conversation ${conversation?.id === item.id ? "is-active" : ""}`}
                    onClick={() => selectConversation(item.id)}>
                    <span>{item.title || "Percakapan tanpa judul"}</span>
                    <small>{relativeDate(item.last_message_at)}</small>
                  </button>
                ))}
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: "1rem",
                  left: "1rem",
                  right: "1rem",
                }}
                className="">
                <UsageBadge status={status} />
                {/* <span className="ai-assistant-outlet">
                    <i className="ti ti-building-store" /> {selectedScope?.label || "Memuat scope"}
                  </span> */}
              </div>
            </aside>

            <section className="ai-assistant-chat" aria-label="Percakapan dengan Akasya">
              <div className="ai-assistant-chat-head">
                <button
                  type="button"
                  className="ai-assistant-history-toggle"
                  aria-label="Buka riwayat percakapan"
                  aria-controls="ai-assistant-history"
                  aria-expanded={isHistoryOpen}
                  onClick={() => setIsHistoryOpen(true)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-layout-sidebar-left-expand">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -12" />
                    <path d="M9 4v16" />
                    <path d="M14 10l2 2l-2 2" />
                  </svg>
                </button>
                <div className="ai-assistant-avatar">
                  {/* <i className="ti ti-brain" /> */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-message-chatbot">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12" />
                    <path d="M9.5 9h.01" />
                    <path d="M14.5 9h.01" />
                    <path d="M9.5 13a3.5 3.5 0 0 0 5 0" />
                  </svg>
                </div>
                <div className="ai-assistant-chat-identity d-sm-block d-none">
                  <strong>Akasya AI</strong>
                  <span>
                    <i className="ti ti-circle-filled" /> Membaca {selectedScope?.label || "scope data"}
                  </span>
                </div>
                <label className="ai-assistant-scope-picker">
                  <span>Scope data</span>
                  <AkasSelect
                    value={selectedScope?.type === "all" ? "all" : selectedScope?.outlet_id || ""}
                    onChange={changeScope}
                    disabled={!scopeOptions}
                    aria-label="Pilih scope data Akasya">
                    {scopeOptions?.can_select_all ? <option value="all">Semua outlet</option> : null}
                    {(scopeOptions?.outlets || []).map((outlet) => (
                      <option key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </option>
                    ))}
                  </AkasSelect>
                </label>
              </div>

              <div className="ai-assistant-messages" aria-live="polite">
                {messages.length === 0 ? (
                  <div className="ai-assistant-welcome">
                    <h3>
                      Mulai dari kondisi hari ini
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-robot ms-3">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M6 6a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2l0 -4" />
                        <path d="M12 2v2" />
                        <path d="M9 12v9" />
                        <path d="M15 12v9" />
                        <path d="M5 16l4 -2" />
                        <path d="M15 14l4 2" />
                        <path d="M9 18h6" />
                        <path d="M10 8v.01" />
                        <path d="M14 8v.01" />
                      </svg>
                    </h3>
                    <p>
                      Saya dapat menyiapkan briefing dan prioritas harian, mendiagnosis perubahan omzet atau
                      profit,memproyeksikan arus kas, serta membaca kondisi usaha .
                    </p>
                    <div className="ai-assistant-quick-replies">
                      {quickReplies.map((reply) => (
                        <button type="button" key={reply} disabled={!canSend} onClick={() => sendMessage(reply)}>
                          {reply}
                          <i className="ti ti-arrow-up-right" />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((item) => (
                    <div className={`ai-assistant-message ai-assistant-message-${item.role}`} key={item.id}>
                      <div className="ai-assistant-message-content">
                        <AiMarkdown content={item.content} />
                      </div>
                      {item.role === "assistant" ? <AiVisualizations items={item.visualizations} /> : null}
                      {messageActionDrafts(item).map((action) => (
                        <ActionDraftCard
                          key={action.id}
                          action={action}
                          busy={updatingActionId === action.id}
                          onDecision={updateActionDraft}
                          canConfirm={Boolean(status?.can_confirm_actions)}
                        />
                      ))}
                      <div className="ai-assistant-message-meta">
                        <small>
                          {item.status === "sending"
                            ? "Mengirim..."
                            : item.status === "failed"
                              ? "Belum terkirim"
                              : dateTime(item.created_at)}
                        </small>
                        <MessageActions
                          message={item}
                          copied={copiedMessageId === item.id}
                          busy={updatingFeedbackId === item.id}
                          onCopy={copyMessage}
                          onRate={rateMessage}
                          onExplain={explainFeedback}
                        />
                      </div>
                      {item.role === "assistant" && feedbackDraft.messageId === item.id ? (
                        <FeedbackDetailPanel
                          draft={feedbackDraft}
                          busy={updatingFeedbackId === item.id}
                          error={feedbackError.messageId === item.id ? feedbackError.message : ""}
                          onChange={updateFeedbackDraft}
                          onSave={saveFeedbackDetails}
                          onClose={closeFeedbackDetails}
                        />
                      ) : null}
                    </div>
                  ))
                )}
                {sending ? (
                  <div className="ai-assistant-thinking">
                    <span />
                    <span />
                    <span /> Akasya sedang menganalisa...
                  </div>
                ) : null}
                <div ref={messagesEndRef} />
              </div>

              {!status?.available || (!status?.is_unlimited && remainingThisWeek(status) <= 0) ? (
                <div className="ai-assistant-limit-notice">
                  <div>
                    <strong>{status?.available ? "Kuota AI minggu ini telah habis" : "Akasya belum tersedia"}</strong>
                    <span>
                      {status?.available
                        ? "Kuota akan tersedia kembali setiap Senin pukul 00.00 WIB."
                        : "Kelola paket dan add-on untuk menyesuaikan kapasitas AI."}
                    </span>
                  </div>
                  <Link to={PATH_URL.MASTER_STORES} className="btn btn-outline-primary">
                    Kelola paket
                  </Link>
                </div>
              ) : null}

              <div className="ai-assistant-composer">
                <label className="visually-hidden" htmlFor="ai-assistant-message">
                  Pertanyaan untuk Akasya
                </label>
                <textarea
                  id="ai-assistant-message"
                  value={draft}
                  disabled={!canSend}
                  onChange={(event) => {
                    setDraft(event.target.value);
                    event.currentTarget.style.height = "auto";
                    event.currentTarget.style.height = `${Math.min(event.currentTarget.scrollHeight, 120)}px`;
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={promptHint}
                  rows="1"
                />
                <button
                  type="button"
                  className="btn btn-primary ai-assistant-send text-white justify-content-center d-flex align-items-center"
                  disabled={!draft.trim() || !canSend}
                  onClick={() => sendMessage()}
                  aria-label="Kirim pesan">
                  {sending ? (
                    <span className="spinner-border spinner-border-sm text-white" />
                  ) : (
                    <span className="d-flex align-items-center justify-content-center m-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-send-2 me-0">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M4.698 4.034l16.302 7.966l-16.302 7.966a.503 .503 0 0 1 -.546 -.124a.555 .555 0 0 1 -.12 -.568l2.468 -7.274l-2.468 -7.274a.555 .555 0 0 1 .12 -.568a.503 .503 0 0 1 .546 -.124" />
                        <path d="M6.5 12h14.5" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
              {/* <p className="ai-assistant-composer-note">Estimasikan omzet pada bulan depan ...</p> */}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiAssistantPage;
